import { useNavigate } from "react-router-dom"
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@shared/components/ui/command"
import { useAuthStore } from "@features/auth/hooks/useAuthStore"
import { getNavigationConfig } from "@shared/lib/configs/navigation-config"
import { useKeyboardShortcuts, COMMON_SHORTCUTS } from "@shared/hooks/useKeyboardShortcuts"
import type { LucideIcon } from "lucide-react"

interface SearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SearchItem {
  title: string
  url: string
  icon: LucideIcon
  section: string
}

export function SearchModal({ open, onOpenChange }: SearchModalProps) {
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.role)
  const { main, services, servicesLabel } = getNavigationConfig(role)

  useKeyboardShortcuts([
    {
      ...COMMON_SHORTCUTS.SEARCH,
      action: () => onOpenChange(true),
    },
  ])

  const items: SearchItem[] = [
    // Main nav items
    ...main.map((item) => ({
      title: item.title,
      url: item.url,
      icon: item.icon,
      section: "Platform",
    })),
    // Services items — expand children if present
    ...services.flatMap((item) => {
      if (item.children?.length) {
        return item.children.map((child) => ({
          title: `${item.title} / ${child.title}`,
          url: child.url,
          icon: item.icon,
          section: servicesLabel,
        }))
      }
      return [
        {
          title: item.title,
          url: item.url,
          icon: item.icon,
          section: servicesLabel,
        },
      ]
    }),
  ]

  const platformItems = items.filter((i) => i.section === "Platform")
  const serviceItems = items.filter((i) => i.section === servicesLabel)

  function handleSelect(url: string) {
    navigate(url)
    onOpenChange(false)
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages and actions..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Platform">
          {platformItems.map((item) => (
            <CommandItem
              key={item.url}
              value={item.title}
              onSelect={() => handleSelect(item.url)}
            >
              <item.icon className="mr-2 h-4 w-4 shrink-0" />
              {item.title}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading={servicesLabel}>
          {serviceItems.map((item) => (
            <CommandItem
              key={`${item.section}-${item.url}-${item.title}`}
              value={item.title}
              onSelect={() => handleSelect(item.url)}
            >
              <item.icon className="mr-2 h-4 w-4 shrink-0" />
              {item.title}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  )
}
